import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { getNews, getAnnouncements } from "../../services/api";
import type { Actualite, Announcement } from "../../types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faNewspaper,
  faArrowRight,
  faSearch,
  faBullhorn,
  faVideo,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { useLanguage } from "../../context/LanguageContext";
import { resolveMediaUrl } from "../../utils/media";

const News: React.FC = () => {
  const { language } = useLanguage();
  const [newsItems, setNewsItems] = useState<Actualite[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 6;

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setAnnouncementsLoading(true);

      const [newsResponse, announcementsResponse] = await Promise.all([
        getNews(),
        getAnnouncements(),
      ]);

      // Sort news by date in descending order (most recent first)
      const sortedNews = newsResponse.data.sort(
        (a: Actualite, b: Actualite) => {
          const dateA = new Date(a.datepublication || 0).getTime();
          const dateB = new Date(b.datepublication || 0).getTime();
          return dateB - dateA; // Descending order
        }
      );

      setNewsItems(sortedNews);
      setAnnouncements(announcementsResponse.data.slice(0, 5)); // Last 5 announcements
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setAnnouncementsLoading(false);
    }
  };

  // تنظيف النص من الوسوم HTML
  const cleanHtmlTags = (text: string) => {
    if (!text) return "";
    return text
      .replace(/<p><p>/g, "")
      .replace(/<p>/g, "")
      .replace(/<\/p>/g, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/&nbsp;/g, " ")
      .replace(/<[^>]*>/g, "")
      .trim();
  };

  // Check if news contains YouTube URL
  const containsYouTubeUrl = (text: string | undefined): boolean => {
    if (!text) return false;
    const youtubeRegex =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=)?([a-zA-Z0-9_-]+)/;
    return youtubeRegex.test(text);
  };

  // Get appropriate content based on language
  const getNewsTitle = (news: Actualite) => {
    return language === "ar"
      ? news.titrearabe
      : news.titrefrancais || news.titrearabe;
  };

  const getNewsText = (news: Actualite) => {
    return language === "ar"
      ? news.textarabe
      : news.textfrancais || news.textarabe;
  };

  const getAnnouncementTitle = (announcement: Announcement) => {
    return language === "ar"
      ? announcement.titrearabe
      : announcement.titrefrancais || announcement.titrearabe;
  };

  // البحث في الأخبار مع الحفاظ على الترتيب
  const filteredNews = useMemo(() => {
    if (!searchTerm) return newsItems;

    const filtered = newsItems.filter(
      (news) =>
        (news.titrearabe?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        ) ||
        (news.titrefrancais?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        ) ||
        (news.textarabe?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        ) ||
        (news.textfrancais?.toLowerCase() || "").includes(
          searchTerm.toLowerCase()
        )
    );

    return filtered;
  }, [newsItems, searchTerm]);

  // حساب عدد الصفحات
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);

  // الحصول على الأخبار للصفحة الحالية
  const currentNews = filteredNews.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Format date function
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(
      language === "ar" ? "ar-MA" : "fr-FR",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
      }
    );
  };

  // Direction and text alignment
  const getDirectionClass = () => (language === "ar" ? "rtl" : "ltr");
  const getTextAlignClass = () =>
    language === "ar" ? "text-right" : "text-left";

  // Translations
  const translations = {
    // Hero Section
    ministryNews:
      language === "ar" ? "أخبار الوزارة" : "Actualités du Ministère",
    newsDescription:
      language === "ar"
        ? "آخر الأخبار والتطورات في وزارة العقارات وأملاك الدولة"
        : "Dernières actualités et développements du Ministère des Domaines et des Affaires Foncières",

    // Search
    searchPlaceholder:
      language === "ar"
        ? "ابحث في الأخبار..."
        : "Rechercher dans les actualités...",

    // Loading States
    loadingNews:
      language === "ar"
        ? "جاري تحميل الأخبار..."
        : "Chargement des actualités...",
    noNews:
      language === "ar" ? "لا توجد أخبار متاحة" : "Aucune actualité disponible",
    noSearchResults:
      language === "ar"
        ? "لا توجد أخبار تطابق بحثك"
        : "Aucune actualité ne correspond à votre recherche",

    // News Items
    newsLabel: language === "ar" ? "خبر" : "Actualité",
    readMore: language === "ar" ? "قراءة المزيد" : "Lire la suite",
    undefinedDate:
      language === "ar" ? "غير محدد التاريخ" : "Date non spécifiée",

    // Pagination
    previous: language === "ar" ? "السابق" : "Précédent",
    next: language === "ar" ? "التالي" : "Suivant",

    // Sidebar Sections
    lastAnnouncements:
      language === "ar" ? "آخر الإعلانات" : "Dernières Annonces",
    viewAll: language === "ar" ? "عرض الكل" : "Voir tout",
    noAnnouncements: language === "ar" ? "لا توجد إعلانات" : "Aucune annonce",
    viewAllAnnouncements:
      language === "ar" ? "عرض جميع الإعلانات" : "Voir toutes les annonces",
  };

  return (
    <div className={`min-h-screen ${getDirectionClass()}`}>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-mauritania-green-dark to-mauritania-green text-white py-20 text-center">
        <div className="container mx-auto px-4">
          <div
            className={`flex items-center justify-center mb-4 ${
              language === "ar" ? "flex-row" : "flex-row-reverse"
            }`}
          >
            <FontAwesomeIcon
              icon={faNewspaper}
              className="text-white text-4xl mx-3"
            />
            <h1 className="text-4xl font-bold">{translations.ministryNews}</h1>
          </div>
          <p className="text-xl">{translations.newsDescription}</p>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content - News Grid */}
          <div className="lg:w-3/4">
            {/* Search Bar for Mobile */}
            <div className="lg:hidden mb-6">
              <div className="relative">
                <div
                  className={`absolute inset-y-0 ${
                    language === "ar" ? "right-0" : "left-0"
                  } flex items-center ${
                    language === "ar" ? "pr-3" : "pl-3"
                  } pointer-events-none`}
                >
                  <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  className={`w-full ${
                    language === "ar" ? "pr-10 pl-3" : "pl-10 pr-3"
                  } py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:border-transparent`}
                  placeholder={translations.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-16">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="text-mauritania-green-dark text-4xl animate-spin mb-4"
                />
                <p className="text-gray-600">{translations.loadingNews}</p>
              </div>
            ) : currentNews.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-md">
                <p className="text-gray-600">
                  {searchTerm
                    ? translations.noSearchResults
                    : translations.noNews}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {currentNews.map((news) => {
                    const newsText = getNewsText(news);
                    const hasYouTubeVideo = newsText
                      ? containsYouTubeUrl(newsText)
                      : false;

                    return (
                      <div
                        key={news.id}
                        className="bg-white rounded-lg overflow-hidden shadow-custom hover:shadow-xl transition-all duration-300 hover:-translate-y-2 flex flex-col h-full"
                      >
                        {/* Image container with fixed aspect ratio */}
                        <div className="relative h-0 pb-[56.25%] overflow-hidden">
                          <Link
                            to={`/news/${news.id}`}
                            className="relative h-0 pb-[56.25%] overflow-hidden block group"
                          >
                            <img
                              src={
                                resolveMediaUrl(news.image) ||
                                "https://images.unsplash.com/photo-1581093450020-7c01c5c81a2b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                              }
                              alt={getNewsTitle(news)}
                              className="absolute inset-0 w-full h-full object-cover transition duration-700 hover:scale-110"
                            />
                          </Link>
                          {/* News label in one corner */}
                          <div
                            className={`absolute top-4 ${
                              language === "ar" ? "left-4" : "right-4"
                            } bg-mauritania-gold text-mauritania-green-dark font-semibold py-1 px-3 rounded-full text-sm`}
                          >
                            {translations.newsLabel}
                          </div>

                          {/* Video icon in opposite corner if news contains YouTube URL */}
                          {hasYouTubeVideo && (
                            <div
                              className={`absolute top-4 ${
                                language === "ar" ? "right-4" : "left-4"
                              } bg-red-600 text-white p-2 rounded-full shadow-lg`}
                              title={
                                language === "ar"
                                  ? "يحتوي على فيديو"
                                  : "Contient une vidéo"
                              }
                            >
                              <FontAwesomeIcon
                                icon={faVideo}
                                className="text-lg"
                              />
                            </div>
                          )}
                        </div>
                        <div className="p-6 flex flex-col flex-grow">
                          <div
                            className={`flex items-center text-sm text-mauritania-gold mb-3 ${getTextAlignClass()}`}
                          >
                            <FontAwesomeIcon
                              icon={faCalendarAlt}
                              className={language === "ar" ? "mr-2" : "ml-2"}
                            />
                            {news.datepublication
                              ? formatDate(news.datepublication)
                              : translations.undefinedDate}
                          </div>
                          <h3
                            className={`text-xl font-bold text-mauritania-green-dark mb-4 line-clamp-2 ${getTextAlignClass()}`}
                          >
                            {getNewsTitle(news)}
                          </h3>
                          <div
                            className={`text-gray-600 mb-6 whitespace-pre-line line-clamp-3 flex-grow ${getTextAlignClass()}`}
                          >
                            {cleanHtmlTags(newsText || "").substring(0, 120)}
                            {newsText && newsText.length > 120 ? "..." : ""}
                          </div>
                          <Link
                            to={`/news/${news.id}`}
                            className={`inline-flex items-center text-mauritania-green-dark font-semibold hover:text-mauritania-green transition duration-300 mt-auto ${
                              language === "ar"
                                ? "flex-row"
                                : "flex-row-reverse"
                            }`}
                          >
                            {translations.readMore}
                            <FontAwesomeIcon
                              icon={faArrowRight}
                              className={
                                language === "ar"
                                  ? "mr-2"
                                  : "ml-2 transform rotate-180"
                              }
                            />
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-12">
                    <nav className="flex items-center space-x-2">
                      {currentPage > 1 && (
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          className={`px-4 py-2 rounded-md bg-mauritania-green text-white hover:bg-mauritania-green-dark transition duration-300 flex items-center ${
                            language === "ar" ? "flex-row" : "flex-row-reverse"
                          }`}
                        >
                          {translations.previous}
                          <FontAwesomeIcon
                            icon={faArrowRight}
                            className={
                              language === "ar"
                                ? "mr-2"
                                : "ml-2 transform rotate-180"
                            }
                          />
                        </button>
                      )}

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-4 py-2 rounded-md ${
                              currentPage === page
                                ? "bg-mauritania-green text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-mauritania-gold"
                            } transition duration-300`}
                          >
                            {page}
                          </button>
                        )
                      )}

                      {currentPage < totalPages && (
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          className={`px-4 py-2 rounded-md bg-mauritania-green text-white hover:bg-mauritania-green-dark transition duration-300 flex items-center ${
                            language === "ar" ? "flex-row" : "flex-row-reverse"
                          }`}
                        >
                          {translations.next}
                          <FontAwesomeIcon
                            icon={faArrowRight}
                            className={
                              language === "ar"
                                ? "ml-2 transform rotate-180"
                                : "mr-2"
                            }
                          />
                        </button>
                      )}
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/4 space-y-6">
            {/* Search Bar for Desktop */}
            <div className="hidden lg:block">
              <div className="relative">
                <div
                  className={`absolute inset-y-0 ${
                    language === "ar" ? "right-0" : "left-0"
                  } flex items-center ${
                    language === "ar" ? "pr-3" : "pl-3"
                  } pointer-events-none`}
                >
                  <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  className={`w-full ${
                    language === "ar" ? "pr-10 pl-3" : "pl-10 pr-3"
                  } py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:border-transparent`}
                  placeholder={translations.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>

            {/* Last Announcements */}
            <div className="bg-white rounded-lg shadow-custom p-6">
              <div
                className={`flex items-center mb-4 pb-3 border-b border-gray-200 ${getTextAlignClass()}`}
              >
                <FontAwesomeIcon
                  icon={faBullhorn}
                  className="text-mauritania-gold text-xl mx-2"
                />
                <h3 className="text-lg font-bold text-mauritania-green-dark">
                  {translations.lastAnnouncements}
                </h3>
              </div>

              {announcementsLoading ? (
                <div className="text-center py-8">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="text-mauritania-green-dark text-2xl animate-spin"
                  />
                </div>
              ) : announcements.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">
                  {translations.noAnnouncements}
                </p>
              ) : (
                <div className="space-y-4">
                  {announcements.map((announcement) => (
                    <Link
                      key={announcement.id}
                      to={`/announcements/${announcement.id}`}
                      className="block pb-3 border-b border-gray-100 last:border-b-0 last:pb-0 hover:bg-gray-50 -mx-3 px-3 rounded transition-colors duration-200"
                    >
                      <div
                        className={`flex items-start ${
                          language === "ar" ? "space-x-reverse" : "space-x"
                        } space-x-3`}
                      >
                        <div className="flex-shrink-0 w-2 h-2 bg-mauritania-gold rounded-full mt-2"></div>
                        <div className="flex-1 min-w-0">
                          <h4
                            className={`text-sm font-semibold text-mauritania-green-dark line-clamp-2 mb-1 ${getTextAlignClass()} hover:text-mauritania-green transition-colors duration-200`}
                          >
                            {getAnnouncementTitle(announcement)}
                          </h4>
                          <div
                            className={`flex items-center text-xs text-gray-500 ${getTextAlignClass()}`}
                          >
                            <FontAwesomeIcon
                              icon={faCalendarAlt}
                              className={language === "ar" ? "ml-1" : "mr-1"}
                            />
                            {announcement.datepublication
                              ? formatDate(announcement.datepublication)
                              : translations.undefinedDate}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {announcements.length > 0 && (
                <Link
                  to="/announcements"
                  className={`block mt-4 text-center text-mauritania-green hover:text-mauritania-green-dark font-semibold text-sm ${
                    language === "ar" ? "flex-row" : "flex-row-reverse"
                  } flex items-center justify-center`}
                >
                  {translations.viewAllAnnouncements}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className={
                      language === "ar" ? "mr-2" : "ml-2 transform rotate-180"
                    }
                  />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default News;
